import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Edit2, Trash } from "iconsax-react";
import { Button, ModalWrapper, Prompt, PromptForm } from "@/shared/ui";
import { extractErrorMessage } from "@/shared/utils";
import { useGetChatChannelsQuery, useDeleteChatChannelMutation } from "../api";
import type { ChatChannel } from "../types";
import ChatChannelCreateModal from "./ChatChannelCreateModal";
import ChatChannelEditModal from "./ChatChannelEditModal";

interface PromptState {
  title: string;
  text: string;
  variant: "success" | "error";
}

interface Props {
  onClose: () => void;
}

export default function ChatChannelManagementModal({ onClose }: Props) {
  const { t } = useTranslation("ChatPage");
  const { data: channels = [], refetch } = useGetChatChannelsQuery(undefined, { refetchOnFocus: false });
  const [deleteChannel, { isLoading: isDeleting }] = useDeleteChatChannelMutation();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<ChatChannel | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState<PromptState | null>(null);

  async function handleDeleteConfirm(id: number) {
    try {
      await deleteChannel(id).unwrap();
      setPrompt({ title: t("manage.deleteSuccessTitle"), text: t("manage.deleteSuccessText"), variant: "success" });
    } catch (err: unknown) {
      const apiErr = err as { status?: number };
      if (apiErr?.status === 404) {
        refetch();
        setPrompt({ title: t("manage.errorTitle"), text: t("manage.staleError"), variant: "error" });
      } else {
        setPrompt({
          title: t("manage.errorTitle"),
          text: extractErrorMessage(err, "manage.deleteError", t),
          variant: "error",
        });
      }
    } finally {
      setConfirmDeleteId(null);
    }
  }

  if (isCreateOpen) {
    return <ChatChannelCreateModal onClose={() => setIsCreateOpen(false)} />;
  }

  if (editingChannel) {
    return (
      <ChatChannelEditModal
        channel={editingChannel}
        onClose={() => {
          setEditingChannel(null);
          refetch();
        }}
      />
    );
  }

  return (
    <ModalWrapper onClose={onClose} width="w-xl">
      <div className="flex flex-col gap-6 min-h-0">
        <h2 className="text-display-2xs content-base-primary pr-8">{t("manage.title")}</h2>

        {channels.length > 0 ? (
          <div className="flex flex-col gap-2 overflow-y-auto component-scroll-thin max-h-64">
            {channels.map((channel) => (
              <div key={channel.id} className="flex items-center gap-2 px-4 py-3 radius-md surface-secondary-fill">
                <div className="flex-1 min-w-0">
                  <p className="text-body-bold-sm content-base-primary truncate">{channel.display_name}</p>
                  {/* <p className="text-body-regular-xs content-base-secondary truncate">{channel.name}</p> */}
                </div>
                <Button
                  variant="secondary"
                  isIconButton
                  onClick={() => setEditingChannel(channel)}
                  disabled={isDeleting}
                  className="w-7! h-7! p-0! rounded-sm!">
                  <Edit2 size={16} color="currentColor" className="content-base-secondary" />
                </Button>
                <Button
                  variant="danger"
                  isIconButton
                  onClick={() => setConfirmDeleteId(channel.id)}
                  disabled={isDeleting}
                  className="w-7! h-7! p-0! rounded-sm!">
                  <Trash size={16} color="currentColor" className="content-action-negative" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-body-regular-sm content-base-secondary">{t("manage.empty")}</p>
        )}

        <Button type="button" variant="primary" size="md" onClick={() => setIsCreateOpen(true)}>
          {t("manage.createTitle")}
        </Button>
      </div>

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="ChatPage"
        />
      )}

      {confirmDeleteId != null && (
        <PromptForm
          title={t("manage.confirmDelete.title")}
          text={t("manage.confirmDelete.text")}
          variant="error"
          onClose={() => setConfirmDeleteId(null)}
          onConfirm={() => handleDeleteConfirm(confirmDeleteId)}
          isLoading={isDeleting}
          confirmText={t("manage.confirmDelete.confirm")}
          cancelText={t("manage.confirmDelete.cancel")}
          namespace="ChatPage"
        />
      )}
    </ModalWrapper>
  );
}

